<script setup lang="ts">
import { ref, computed, watch, onMounted, markRaw } from 'vue'
import { useTodoStore } from '@/stores/useTodoStore'
import { formatDate, dateComparator } from '@/utils/dateUtils'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import TodoModal from '@/components/features/todos/TodoModal.vue'
import TodoStatusBadge from '@/components/features/todos/TodoStatusBadge.vue'
import TodoGridActions from '@/components/features/todos/TodoGridActions.vue'

const props = defineProps<{
  companyUuid: string
  company: { uuid: string; name: string }
}>()

const emit = defineEmits<{
  'count-changed': [count: number]
}>()

const todoStore = useTodoStore()

const todos = ref<any[]>([])
const pageSize = ref(50)
const page = ref(1)
const totalTodos = ref(0)
const totalPages = ref(1)
const sort = ref<{ field: string; direction: string } | null>(null)

// Modal édition/vue
const selectedTodo = ref<any | null>(null)
const showModal = ref(false)

// Modal copie
const copyTodoData = ref<any | null>(null)
const showCopyModal = ref(false)

const SORTABLE_FIELDS = new Set(['title', 'status', 'priority', 'type', 'dueDate'])

const gridColumns = computed(() => [
  {
    field: 'title',
    headerName: 'Titre',
    cellClass: 'border-0',
    filter: false,
  },
  {
    field: 'type',
    headerName: 'Type',
    cellClass: 'border-0',
    filter: false,
    valueFormatter: (params: any) => {
      const labels: Record<string, string> = {
        TASK: 'Tâche', REMINDER: 'Rappel', CALL: 'Appel',
        EMAIL: 'Email', MEETING: 'Réunion', FOLLOW_UP: 'Suivi',
      }
      return labels[params.value] || params.value || '-'
    },
  },
  {
    field: 'status',
    headerName: 'Statut',
    cellClass: 'border-0',
    filter: false,
    cellRenderer: markRaw(TodoStatusBadge),
  },
  {
    field: 'priority',
    headerName: 'Priorité',
    cellClass: 'border-0',
    filter: false,
    valueFormatter: (params: any) => {
      const labels: Record<string, string> = {
        LOW: 'Basse', MEDIUM: 'Moyenne', HIGH: 'Haute', URGENT: 'Urgente',
      }
      return labels[params.value] || params.value || '-'
    },
  },
  {
    field: 'dueDate',
    headerName: 'Échéance',
    cellClass: 'border-0',
    filter: false,
    valueFormatter: (params: any) => (params.value ? formatDate(params.value.toString()) : '-'),
    comparator: dateComparator,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    resizable: false,
    filter: false,
    sortable: false,
    minWidth: 120,
    maxWidth: 150,
    cellClass: 'border-0 grid-action',
    cellRenderer: markRaw(TodoGridActions),
    cellRendererParams: {
      tooltipPosition: 'left',
      onClickView: (todo: any) => handleViewTodo(todo),
    },
  },
])

async function fetchTodos() {
  const filters: Record<string, any> = { companyUuid: props.companyUuid }
  if (sort.value) filters.sort = [sort.value]

  const result = await todoStore.fetchTodos(filters, pageSize.value, page.value)
  if (result) {
    todos.value = result.data || []
    totalTodos.value = result.total || 0
    totalPages.value = result.totalPages || 1
    const pendingCount = todos.value.filter((t) => t.status === 'PENDING').length
    emit('count-changed', pendingCount)
  }
}

function handleViewTodo(todo: any) {
  selectedTodo.value = todo
  showModal.value = true
}

function closeModal() {
  selectedTodo.value = null
  showModal.value = false
}

function closeCopyModal() {
  copyTodoData.value = null
  showCopyModal.value = false
}

function onTodoUpdated() {
  fetchTodos()
  closeModal()
}

function onTodoCreated() {
  fetchTodos()
}

function onCompleteAndCopy(data: any) {
  fetchTodos()
  closeModal()
  copyTodoData.value = data
  showCopyModal.value = true
}

function onSortChanged(sortEvent: { colId: string; sort: string } | null) {
  sort.value = sortEvent && SORTABLE_FIELDS.has(sortEvent.colId)
    ? { field: sortEvent.colId, direction: sortEvent.sort }
    : null
  fetchTodos()
}

async function onPaginationChanged(newPageSize: number, newPage: number) {
  if (pageSize.value === newPageSize && page.value === newPage) return
  if (pageSize.value !== newPageSize) pageSize.value = newPageSize
  if (page.value !== newPage) page.value = newPage
  await fetchTodos()
}

watch(() => props.companyUuid, (uuid) => { if (uuid) fetchTodos() })
onMounted(() => { if (props.companyUuid) fetchTodos() })
</script>

<template>
  <div>
    <GridVue
      :list="todos"
      :cols="gridColumns"
      :get-row-height="(_params: any) => 48"
      :use-server-side-pagination="true"
      :pagination-page-size="pageSize"
      :pagination-total-rows="totalTodos"
      :current-page="page"
      :total-pages="totalPages"
      @onPaginationChanged="onPaginationChanged"
      @onSortChanged="onSortChanged"
    >
      <template #title>
        <h2 class="card-title">Tâches</h2>
      </template>
      <template #button>
        <div class="mb-1">
          <TodoModal :company="company" @created="onTodoCreated" />
        </div>
      </template>
    </GridVue>

    <!-- Modal édition/vue -->
    <TodoModal
      v-if="selectedTodo"
      :todo="selectedTodo"
      :show-modal="showModal"
      @close="closeModal"
      @updated="onTodoUpdated"
      @complete-and-copy="onCompleteAndCopy"
    />

    <!-- Modal création par copie -->
    <TodoModal
      v-if="copyTodoData"
      :todo="copyTodoData"
      mode="create"
      :show-modal="showCopyModal"
      @close="closeCopyModal"
      @created="() => { fetchTodos(); closeCopyModal() }"
    />
  </div>
</template>
